import { useState, CSSProperties, useContext } from 'react'
import SubscriptionDetailsTile from './SubscriptionDetailsTile'
import useScreen from 'Hooks/useScreen'
import automatedMarketingTools from 'assets/images/automated-marketing-tools.png'
import automatedMarketingToolsMobile from 'assets/images/automated-marketing-tools-mobile.png'
import { BillingCycle } from './shareable/UpgradeToProModal/utils'
import { PlanType, DEFAULT_CURRENCY } from 'modules/Const'
import CurrencyDropdown from './shareable/CurrencyDropdown'
import useBrand, { BrandExistsState } from 'Hooks/useBrand'
import Colors from 'modules/Colors'
import ButtonSymphony from '../elements/ButtonSymphony'
import * as Sentry from "@sentry/react";
import { toast } from 'react-toastify'
import { SHARED_TOAST_OPTIONS_ERROR } from 'modules/Toast'
import { UpgradeModalContext } from 'Hooks/UpgradeModalContext'
import { clsx, goToUrl, styx } from 'modules/Utils'
import { getCancelSubscriptionPortalUrl_api, getStripeSubscriptionPortalUrl_api } from 'api/subscriptionApi'
import UnifiedPaymentForm from 'elements/UnifiedPaymentForm'
import { PaymentData } from 'elements/UnifiedPaymentForm'
import { ConfirmPaymentResponse } from 'api/paymentsV2Api'
import { showToast } from 'modules/Utils'

// Subscription step type
type SubscriptionStep = 'selectPlan' | 'payment'

// Selected plan data for payment step
interface SelectedPlan {
    planType: PlanType
    billingCycle: BillingCycle
}

type SubscriptionDetailsProps = {
    className?: string;
    style?: CSSProperties;
    /** Optional callback for skipping subscription */
    handleSkipSubscribe?: (() => void) | undefined;
    /** Optional custom call to action text for upgrade */
    upgradeCallToAction?: string;
    /** Optional subtitle for upgrade call to action */
    upgradeCallToActionSubtitle?: string;
    /** Optional plan to highlight */
    highlightedPlan?: PlanType;
    /** Optional plan to show in the list */
    onlyShowPlans?: PlanType[];
    /** Source of the checkout */
    checkout_source?: string;
    /** Callback function to be called when the plan changes successfully */
    onPlanChangedSuccess?: (tier: PlanType) => void;
    /** Whether this is a downgrade flow */
    isDowngrade?: boolean;
    /** Whether to show the cancel subscription button */
    showCancelSubscription?: boolean;
    /** Whether to show the update payment method button */
    showUpdatePaymentMethod?: boolean;
    /** Default recurring interval to show */
    defaultRecurringInterval?: BillingCycle;
    /** URL to redirect to after the subscription is updated */
    successUrl?: string;
    /** URL to redirect to after the subscription is updated */
    cancelUrl?: string;
    /** Whether the user is on a grandfathered plan */
    isGrandfathered?: boolean;
    /** Callback to reset subscription step when modal closes */
    onModalClose?: () => void;
}

const SubscriptionDetails = ({
    className = '',
    style = {},
    handleSkipSubscribe,
    upgradeCallToAction,
    upgradeCallToActionSubtitle,
    highlightedPlan,
    onlyShowPlans = [],
    checkout_source = '',
    onPlanChangedSuccess,
    isDowngrade = false,
    showUpdatePaymentMethod = false,
    showCancelSubscription,
    defaultRecurringInterval = BillingCycle.MONTHLY,
    successUrl,
    cancelUrl,
    onModalClose,
    // isGrandfathered prop is passed from parent, but we'll also get it from useBrand for internal logic
}: SubscriptionDetailsProps) => {
    const { mobileView, mStyle } = useScreen()
    const { currentBrand, isGrandfathered, getBrand } = useBrand() as BrandExistsState // Fetch isGrandfathered here
    const { handleOpenModal } = useContext(UpgradeModalContext);

    const [billingCycle, setBillingCycle] = useState(defaultRecurringInterval)
    const [isLoading, setIsLoading] = useState(false)
    
    // New state for subscription steps
    const [subscriptionStep, setSubscriptionStep] = useState<SubscriptionStep>('selectPlan')
    const [selectedPlan, setSelectedPlan] = useState<SelectedPlan | null>(null)

    // Reset step when modal closes
    const resetSubscriptionStep = () => {
        setSubscriptionStep('selectPlan')
        setSelectedPlan(null)
    }

    // Call parent callback and reset step
    const handleModalClose = () => {
        resetSubscriptionStep()
        onModalClose?.()
    }

    /**
     * Handles toggling between billing cycles
     * @param {BillingCycle} billingCycleLocal - The billing cycle to switch to
     */
    const handleBillingCycleToggle = (billingCycleLocal: BillingCycle) => {
        setBillingCycle(billingCycleLocal)
    }

    /**
     * Handles plan selection for free users - moves to payment step instead of redirecting
     */
    const handlePlanSelection = (planType: PlanType, currentBillingCycle: BillingCycle) => {
        setSelectedPlan({
            planType,
            billingCycle: currentBillingCycle
        })
        setSubscriptionStep('payment')
    }

    /**
     * Handles successful payment completion
     */
    const handlePaymentSuccess = async (result: ConfirmPaymentResponse) => {
        if (result.success) {
            await getBrand(currentBrand?.id);
            // Refresh brand data to get updated subscription info            
            showToast({
                message: result.message || 'Subscription activated successfully!',
                error: false,
            })
            
            // Call success callback
            if (onPlanChangedSuccess && selectedPlan) {
                onPlanChangedSuccess(selectedPlan.planType)
            }
            
            // Reset state
            onModalClose?.()
        }
    }

    /**
     * Handles payment errors
     */
    const handlePaymentError = (error: string) => {
        showToast({
            message: error,
            error: true,
        })
    }

    /**
     * Handles back button from payment step
     */
    const handleBackToSelection = () => {
        setSubscriptionStep('selectPlan')
        setSelectedPlan(null)
    }

    const onCancelSubscriptionPortal = async () => {
        try {
            setIsLoading(true);
            const portalUrl = await getCancelSubscriptionPortalUrl_api(currentBrand.slug);
            goToUrl(portalUrl, setIsLoading);
        } catch (error) {
            Sentry.captureException(error);
            console.error("Error getting cancel subscription portal: ", error);
            toast.error("Something went wrong. Please try again or contact support.", SHARED_TOAST_OPTIONS_ERROR);
        } finally {
            setIsLoading(false);
        }
    }

    const onUpdatePaymentMethod = async () => {
        const retriesFailed = Boolean(currentBrand?.subscription?.retriesFailed)
        try {
            if (retriesFailed) {
                handleOpenModal({
                    source: "Payment Failed Banner - Reactivate Pro Button"
                })
            } else {
                const url = await getStripeSubscriptionPortalUrl_api(currentBrand.slug);
                window.location.href = url;
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error("Error getting cancel subscription portal: ", error);
            toast.error("Something went wrong. Please try again or contact support.", SHARED_TOAST_OPTIONS_ERROR);
        }
    }

    const renderTitle = () => {
        // Different title for payment step
        if (subscriptionStep === 'payment' && selectedPlan) {
            return (
                <div className="subscriptionDetailsTitleContainer" style={sty.subscriptionDetailsTitleContainer}>
                    <div
                        className="subscriptionDetailsTitle"
                        style={mStyle(sty, 'subscriptionDetailsTitle')}
                    >
                        Finish checkout
                    </div>
                </div>
            )
        }

        if (isDowngrade) {
            return (
                <div className="subscriptionDetailsTitleContainer">
                    <div
                        className="subscriptionDetailsTitle"
                        style={mStyle(sty, 'subscriptionDetailsTitle')}
                    >
                        Update your subscription with ease.
                    </div>
                </div>
            )
        }
        if (upgradeCallToAction) {
            return (
                <div className="subscriptionDetailsTitleContainer" style={sty.subscriptionDetailsTitleContainer}>
                    <div
                        className="subscriptionDetailsTitle"
                        style={mStyle(sty, 'subscriptionDetailsTitle')}
                    >
                        {upgradeCallToAction}
                    </div>
                    {upgradeCallToActionSubtitle && (
                        <div className="subscriptionDetailsTitleSubtitle"
                            style={mStyle(sty, 'subscriptionDetailsTitleSubtitle')}
                        >
                            {upgradeCallToActionSubtitle}
                        </div>
                    )}
                </div>
            )
        }

        return (
            <div className="subscriptionDetailsTitleContainer" style={sty.subscriptionDetailsTitleContainer}>
                <div
                    className="subscriptionDetailsTitle"
                    style={mStyle(sty, 'subscriptionDetailsTitle')}
                >
                    Unlock the full power of Symphony's
                </div>
                <div>
                    <img
                        className="subscriptionDetailsTitleImage"
                        style={{
                            width: '100%',
                            maxWidth: mobileView ? 260 : 520,
                        }}
                        src={mobileView ? automatedMarketingToolsMobile : automatedMarketingTools}
                        alt="Automated Marketing Tools"
                    />
                </div>
            </div>
        )
    }

    const renderBillingCycleToggle = () => {
        return (
            <div
                className="subscriptionDetailsMonthlyToggleContainer"
                style={sty.subscriptionDetailsMonthlyToggleContainer}
            >
                <div
                    className="subscriptionDetailsMonthlyToggle"
                    style={{
                        ...sty.subscriptionDetailsMonthlyToggle,
                        ...(billingCycle === BillingCycle.MONTHLY ? sty.isSelected : {}),
                        ...{ borderRight: `1px solid ${Colors.DARK_DIVIDER_LINE}` },
                        ...(mobileView ? { padding: 8, fontSize: 14 } : {}),
                    }}
                    onClick={() => handleBillingCycleToggle(BillingCycle.MONTHLY)}
                >
                    {mobileView ? 'Monthly' : 'Billed monthly'}
                </div>
                <div
                    className="subscriptionDetailsMonthlyToggle"
                    style={styx(
                        sty.subscriptionDetailsMonthlyToggle,
                        billingCycle === BillingCycle.ANNUAL ? sty.isSelected : {},
                        mobileView ? { padding: 8, fontSize: 14 } : {}
                    )}
                    onClick={() => handleBillingCycleToggle(BillingCycle.ANNUAL)}
                >
                    {mobileView ? 'Annually (25% off)' : 'Billed annually (25% off)'}
                </div>
            </div>
        )
    }

    const renderCurrencyDropdown = () => {
        return (
            <CurrencyDropdown
                className="subscriptionDetailsCurrencyDropdown"
                style={{ padding: 0 }}
                initialCurrency={currentBrand?.currency?.code || DEFAULT_CURRENCY}
                hideTitle
            />
        )
    }

    const renderSubscriptionTiles = () => {
        /** Determines which subscription plans to display based on onlyShowPlans prop and grandfathered status */
        let determinedPlans: PlanType[];
        if (onlyShowPlans && onlyShowPlans.length > 0) {
            determinedPlans = onlyShowPlans;
        } else if (isGrandfathered) {
            determinedPlans = [PlanType.LITE, PlanType.PRO, PlanType.TEAM];
        } else {
            determinedPlans = [PlanType.STARTER, PlanType.GROWTH, PlanType.TEAM_V2];
        }

        const plansToShow = determinedPlans;

        const singlePlanStyles = plansToShow.length === 1 ?
            {
                marginTop: 0,
                marginBottom: 0,
                marginLeft: 'auto',
                marginRight: 'auto',
            }
            :
            {}

        return (
            <div
                className="subscriptionDetailsSubscriptionTilesContainer"
                style={styx(
                    mStyle(sty, 'subscriptionDetailsSubscriptionTilesContainer'),
                )}
            >
                {plansToShow.map((plan) => {
                    let isTileMostPopular = false;
                    if (isGrandfathered && plan === PlanType.PRO) {
                        isTileMostPopular = true;
                    } else if (!isGrandfathered && plan === PlanType.GROWTH) {
                        isTileMostPopular = true;
                    }

                    return (
                        <SubscriptionDetailsTile
                            key={plan}
                            subscriptionType={plan}
                            billingCycle={billingCycle}
                            isMostPopular={isTileMostPopular}
                            isHighlighted={highlightedPlan === plan}
                            checkout_source={checkout_source}
                            onPlanChangedSuccess={onPlanChangedSuccess}
                            isDowngrade={isDowngrade}
                            style={singlePlanStyles}
                            onUpdatePaymentMethod={onUpdatePaymentMethod}
                            showUpdatePaymentMethod={showUpdatePaymentMethod}
                            successUrl={successUrl}
                            cancelUrl={cancelUrl}
                            isGrandfathered={isGrandfathered}
                            // New prop for handling plan selection callback
                            onPlanSelection={handlePlanSelection}
                        />
                    );
                })}
            </div>
        )
    }

    const renderPaymentStep = () => {
        if (!selectedPlan) {
            return null
        }

        // Validate that the selected plan is supported by the backend
        // Only current plans are supported: STARTER, GROWTH, TEAM_V2 (LITE, PRO, TEAM are deprecated)
        const supportedPlans = [PlanType.STARTER, PlanType.GROWTH, PlanType.TEAM_V2];
        if (!supportedPlans.includes(selectedPlan.planType)) {
            return (
                <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
                    <p>Error: Selected plan "{selectedPlan.planType}" is not currently supported.</p>
                    <p>Supported plans: STARTER, GROWTH, TEAM_V2</p>
                    <ButtonSymphony className="backToPlansButton" onClick={handleBackToSelection}>← Back to Plans</ButtonSymphony>
                </div>
            );
        }

        // Create payment data for UnifiedPaymentForm
        const paymentData: PaymentData = {
            type: 'subscription',
            planType: selectedPlan.planType,
            billingCycle: selectedPlan.billingCycle,
        }

        return (
            <div className="subscriptionPaymentStep" style={sty.subscriptionPaymentStep}>
                <ButtonSymphony
                    className="backToSelectionButton"  
                    onClick={handleBackToSelection}
                    style={{ alignSelf: 'flex-start', marginBottom: 16 }}
                    variant="outlined"
                >
                    ← Back to Plans
                </ButtonSymphony>

                <UnifiedPaymentForm
                    shouldLoadIntent={true}
                    paymentData={paymentData}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                    onCancel={handleBackToSelection}
                    submitButtonText="Complete Subscription"
                    showCancelButton={false}
                />
            </div>
        )
    }

    // Determine what to render based on current step
    const renderStepContent = () => {
        if (subscriptionStep === 'payment') {
            return renderPaymentStep()
        }

        return (
            <>
                {!isDowngrade && (
                    <div className="billingCycleAndCurrencyContainer" style={sty.billingCycleAndCurrencyContainer}>
                        {renderBillingCycleToggle()}
                        {renderCurrencyDropdown()}
                    </div>
                )}
                {renderSubscriptionTiles()}

                {handleSkipSubscribe &&
                    <ButtonSymphony
                        className="subscriptionDetailsSkipButton"
                        style={{ alignSelf: 'center' }}
                        onClick={handleSkipSubscribe}
                        width="100%"
                        variant="outlined"
                    >
                        Skip for Now
                    </ButtonSymphony>
                }
                {showCancelSubscription &&
                    <ButtonSymphony
                        className="subscriptionDetailsCancelButton"
                        style={{ alignSelf: 'center' }}
                        onClick={onCancelSubscriptionPortal}
                        isLoading={isLoading}
                        variant="outlined"
                        color="red"
                        loadingText="Canceling Subscription"
                    >
                        Cancel Subscription
                    </ButtonSymphony>
                }
            </>
        )
    }

    return (
        <div
            className={clsx('subscriptionDetailsComponent', className)}
            style={styx(
                mStyle(sty, 'subscriptionDetailsComponent'),
                style,
            )}
        >
            {renderTitle()}
            {renderStepContent()}
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    subscriptionDetailsComponent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 32,
        marginLeft: 'auto',
        marginRight: 'auto',
        maxWidth: 932,
        width: '100%'
    },
    subscriptionDetailsComponent_mobile: {
    },
    subscriptionDetailsTitle: {
        fontSize: 40,
        fontWeight: 500,
    },
    subscriptionDetailsTitle_mobile: {
        fontWeight: 500,
        fontSize: 24,
    },
    /** Title subtitle text styles */
    subscriptionDetailsTitleSubtitle: {
        fontSize: 28,
    },
    /** Mobile title subtitle text styles */
    subscriptionDetailsTitleSubtitle_mobile: {
        fontSize: 16,
    },
    subscriptionDetailsTitleContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    },
    /** Billing cycle toggle container styles */
    subscriptionDetailsMonthlyToggleContainer: {
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'row',
        border: `1px solid ${Colors.DARK_DIVIDER_LINE}`,
        borderRadius: 100,
        width: 'fit-content',
        overflow: 'hidden',
        alignSelf: 'flex-start',
    },
    billingCycleAndCurrencyContainer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    subscriptionDetailsSubscriptionTilesContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: 16,
        justifyContent: 'center'
    },
    subscriptionDetailsSubscriptionTilesContainer_mobile: {
        flexDirection: 'column',
    },
    subscriptionDetailsMonthlyToggle: {
        padding: '12px 16px',
    },
    isSelected: {
        color: Colors.purple,
        backgroundColor: Colors.purpleLightest,
    },
    subscriptionPaymentStep: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
}

export default SubscriptionDetails
